import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import PasswordIcon from "@mui/icons-material/Password";
import GoogleIcon from "@mui/icons-material/Google";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login, loginWithGoogle } from "../../services/AuthService";
import { Button, IconButton } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SliderCaptcha from "slider-captcha-react-pimath";
import { localStorageSetItem } from "../../common/localStorage";
import { useGoogleLogin } from "@react-oauth/google";
import "./loading.css";
import { LogIn } from "lucide-react";
import DeviceManagementModal from "../common/DeviceManagementModal";
import backgroundImage from "../../assets/images/bg-page-login.png";
const LoginFormComponent = (props) => {
  const {} = props;
  const navigate = useNavigate();
  const location = useLocation();
  // Ưu tiên: redirectAfterLogin từ sessionStorage > from từ location.state > trang chủ
  const savedRedirectPath = sessionStorage.getItem("redirectAfterLogin");
  const from =
    savedRedirectPath || location.state?.from?.pathname || "/chatbot-ai";
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaUuid, setCaptchaUuid] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (value) {
      setErrors({
        ...errors,
        [name]: false,
      });
    }
  };

  const isValidEmail = (email) =>
    /^[a-zA-Z](?!.*\.\.)[a-zA-Z0-9._-]*[a-zA-Z0-9]@gmail\.com$/.test(email);

  const validate = () => {
    if (!formData.email) {
      toast.error("Vui lòng điền thông tin email.");
      return false;
    }

    if (!isValidEmail(formData.email)) {
      toast.error("Email không đúng định dạng");
      return false;
    }

    if (!formData.password) {
      toast.error("Vui lòng điền thông tin mật khẩu.");
      return false;
    }

    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!captchaToken || !captchaUuid) {
      toast.error("Vui lòng hoàn thành CAPTCHA!");
      return;
    }

    let newErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach((key) => {
      if (!formData[key]) {
        newErrors[key] = true;
        hasErrors = true;
      } else {
        newErrors[key] = false;
      }
    });

    setErrors(newErrors);
    if (!validate()) {
      return;
    }
    if (!hasErrors) {
      try {
        setLoading(true);
        const response = await login({
          ...formData,
          captchaToken,
          captchaUuid,
        });
        if (response) {
          const { username, avatar, email } = response;

          localStorageSetItem("user", {
            username,
            avatar,
            email,
          });

          // Hiển thị thông báo nếu có session khác bị logout
          if (response.message) {
            toast.info(response.message);
          }

          setCaptchaToken("");
          setCaptchaUuid("");
          // Xóa redirect path đã lưu sau khi sử dụng
          if (savedRedirectPath) {
            sessionStorage.removeItem("redirectAfterLogin");
          }
          navigate(from, { replace: true });
        }
      } catch (error) {
        const errorCode = error?.response?.data?.code;
        const message = error?.response?.data?.message || error.message;

        if (errorCode === "MAX_DEVICES_REACHED") {
          setShowDeviceModal(true);
        } else {
          toast.error(message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const verifiedCallback = (token, captcha_uuid) => {
    setCaptchaUuid(captcha_uuid);
    setCaptchaToken(token);
  };


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Remove full screen loading, handle it in button instead

  return (
    <>
      <div
        className="modern-login-container"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        {/* Background Elements */}
        <div className="login-bg-elements">
          <div className="bg-circle bg-circle-1"></div>
          <div className="bg-circle bg-circle-2"></div>
          <div className="bg-circle bg-circle-3"></div>
        </div>

        <div className="login-content">
          {/* Left Side - Illustration */}
          <div className="login-left">
            <div className="illustration-container">
              <img
                src="/images/undraw_book_lover_mkck.svg"
                alt="Login Illustration"
                className="login-illustration"
              />
              <div className="welcome-text">
                <h2>Chào mừng trở lại!</h2>
                <p>Tiếp tục hành trình học tập cùng Pimath</p>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="login-right">
            <div className="login-form-container">
              {/* Header */}
              <div className="login-header">
                <h1 className="text-4xl font-extrabold">Đăng nhập</h1>
              </div>

              {/* Login Form */}
              <form className="login-form" onSubmit={handleLogin}>
                {/* Email Input */}
                <div className="input-group">
                  <div className="input-wrapper">
                    <EmailIcon className="input-icon" />
                    <input
                      type="email"
                      className={`modern-input ${errors.email ? "error" : ""}`}
                      placeholder="Nhập địa chỉ email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div className="input-group">
                  <div className="input-wrapper">
                    <PasswordIcon className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`modern-input ${
                        errors.password ? "error" : ""
                      }`}
                      placeholder="Nhập mật khẩu"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                      disabled={loading}
                    />
                    <IconButton
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </div>
                </div>

                {/* CAPTCHA */}
                <div className="w-[50%]">
                  <SliderCaptcha
                    create="https://api-server.pimath.io.vn/api/captcha/create"
                    verify="https://api-server.pimath.io.vn/api/captcha/verify"
                    // create="http://localhost:4000/api/captcha/create"
                    // verify="http://localhost:4000/api/captcha/verify"
                    callback={verifiedCallback}
                  />
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  className={`login-btn primary-btn bg-gradient-to-r from-[#cd1627] to-[#cd1627] hover:from-[#cd1627] hover:to-[#cd1627] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#cd1627] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                    loading ? "loading" : ""
                  }`}
                  variant="contained"
                  size="medium"
                  startIcon={!loading ? <LogIn /> : null}
                  fullWidth
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loader-auth"></div>
                      <span>Đang đăng nhập...</span>
                    </>
                  ) : (
                    "Đăng nhập"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Device Management Modal */}
      <DeviceManagementModal
        isOpen={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
      />
    </>
  );
};

export default LoginFormComponent;
